<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);
const inboxList = computed(() => getters['inboxes/getAllInboxes'].value);
const teamList = computed(() => getters['teams/getTeams'].value);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));

// Filters state
const filterActionType = ref('');
const filterResourceType = ref('');
const filterUserId = ref('');
const filterSince = ref('');
const filterUntil = ref('');
const expandedRows = ref(new Set());

const currentFilters = computed(() => ({
  actionType: filterActionType.value || undefined,
  resourceType: filterResourceType.value || undefined,
  userId: filterUserId.value || undefined,
  since: filterSince.value || undefined,
  until: filterUntil.value || undefined,
}));

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', { page, ...currentFilters.value });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const applyFilters = () => {
  router.push({ name: 'auditlogs_list', query: { page: 1 } });
  fetchAuditLogs(1);
};

const resetFilters = () => {
  filterActionType.value = '';
  filterResourceType.value = '';
  filterUserId.value = '';
  filterSince.value = '';
  filterUntil.value = '';
  router.push({ name: 'auditlogs_list', query: { page: 1 } });
  fetchAuditLogs(1);
};

const exportCsv = () => {
  store.dispatch('auditlogs/exportCsv', currentFilters.value);
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(
    auditLogItem,
    agentList.value,
    inboxList.value,
    teamList.value
  );
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const getActorName = auditLogItem => {
  if (!auditLogItem.user_id) return t('AUDIT_LOGS.DEFAULT_USER');
  const agent = agentList.value.find(a => a.id === auditLogItem.user_id);
  return agent?.name || auditLogItem.username || String(auditLogItem.user_id);
};

const getActorEmail = auditLogItem => {
  if (!auditLogItem.user_id) return '';
  const agent = agentList.value.find(a => a.id === auditLogItem.user_id);
  return agent?.email || auditLogItem.username || '';
};

const getResourceLabel = auditLogItem => {
  const type = auditLogItem.auditable_type;
  const typeKey = `AUDIT_LOGS.FILTERS.RESOURCE_TYPES.${type}`;
  const label = t(typeKey);
  return label !== typeKey ? label : type;
};

const getResourceName = auditLogItem => {
  const changes = auditLogItem.audited_changes || {};
  const type = auditLogItem.auditable_type?.toLowerCase();
  if (type === 'inbox') {
    const inbox = inboxList.value.find(i => i.id === auditLogItem.auditable_id);
    return inbox?.name || changes.name || `#${auditLogItem.auditable_id}`;
  }
  if (type === 'team') {
    const team = teamList.value.find(tm => tm.id === auditLogItem.auditable_id);
    return team?.name || changes.name || `#${auditLogItem.auditable_id}`;
  }
  if (['macro', 'automationrule', 'webhook'].includes(type)) {
    return changes.name || changes.title || `#${auditLogItem.auditable_id}`;
  }
  if (type === 'accountuser') {
    const agent = agentList.value.find(
      a => a.id === auditLogItem.audited_changes?.user_id
    );
    return agent?.name || `#${auditLogItem.auditable_id}`;
  }
  if (auditLogItem.auditable_id) return `#${auditLogItem.auditable_id}`;
  return '';
};

const toggleExpand = id => {
  const next = new Set(expandedRows.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  expandedRows.value = next;
};

const hasChanges = auditLogItem => {
  const changes = auditLogItem.audited_changes;
  return changes && Object.keys(changes).length > 0;
};

const getChangePairs = auditLogItem => {
  const changes = auditLogItem.audited_changes || {};
  return Object.entries(changes).map(([key, value]) => {
    if (Array.isArray(value)) {
      return { key, before: value[0], after: value[1] };
    }
    return { key, before: null, after: value };
  });
};

const formatChangeValue = val => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page } });
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});

const tableHeaders = computed(() => [
  t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTOR'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.RESOURCE'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
]);

const ACTION_TYPES = ['create', 'update', 'destroy', 'sign_in', 'sign_out'];
const RESOURCE_TYPES = [
  'Inbox',
  'Team',
  'AccountUser',
  'InboxMember',
  'TeamMember',
  'AutomationRule',
  'Macro',
  'Webhook',
  'Account',
  'Conversation',
];
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('AUDIT_LOGS.LOADING')"
    :no-records-found="!records.length && !uiFlags.fetchingList"
    :no-records-message="$t('AUDIT_LOGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUDIT_LOGS.HEADER')"
        :description="$t('AUDIT_LOGS.DESCRIPTION')"
        :link-text="$t('AUDIT_LOGS.LEARN_MORE')"
        feature-name="audit_logs"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <!-- Filters bar -->
        <div
          class="flex flex-wrap gap-3 p-4 bg-n-slate-2 rounded-lg border border-n-slate-5"
        >
          <div class="flex flex-col gap-1 min-w-36">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.ACTION_TYPE') }}
            </label>
            <select
              v-model="filterActionType"
              class="text-sm border border-n-slate-6 rounded px-2 py-1.5 bg-white text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option value="">
                {{ $t('AUDIT_LOGS.FILTERS.ALL_ACTIONS') }}
              </option>
              <option v-for="type in ACTION_TYPES" :key="type" :value="type">
                {{ $t(`AUDIT_LOGS.FILTERS.ACTION_TYPES.${type}`) }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1 min-w-40">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.RESOURCE_TYPE') }}
            </label>
            <select
              v-model="filterResourceType"
              class="text-sm border border-n-slate-6 rounded px-2 py-1.5 bg-white text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option value="">
                {{ $t('AUDIT_LOGS.FILTERS.ALL_RESOURCES') }}
              </option>
              <option
                v-for="rtype in RESOURCE_TYPES"
                :key="rtype"
                :value="rtype"
              >
                {{ $t(`AUDIT_LOGS.FILTERS.RESOURCE_TYPES.${rtype}`) }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1 min-w-40">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.AGENT') }}
            </label>
            <select
              v-model="filterUserId"
              class="text-sm border border-n-slate-6 rounded px-2 py-1.5 bg-white text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option value="">
                {{ $t('AUDIT_LOGS.FILTERS.ALL_AGENTS') }}
              </option>
              <option
                v-for="agent in agentList"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.DATE_FROM') }}
            </label>
            <input
              v-model="filterSince"
              type="date"
              class="text-sm border border-n-slate-6 rounded px-2 py-1.5 bg-white text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.DATE_TO') }}
            </label>
            <input
              v-model="filterUntil"
              type="date"
              class="text-sm border border-n-slate-6 rounded px-2 py-1.5 bg-white text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>

          <div class="flex items-end gap-2">
            <button
              class="px-3 py-1.5 text-sm font-medium bg-n-brand text-white rounded hover:bg-n-brand/90 transition-colors"
              @click="applyFilters"
            >
              {{ $t('AUDIT_LOGS.FILTERS.APPLY') }}
            </button>
            <button
              class="px-3 py-1.5 text-sm font-medium border border-n-slate-6 text-n-slate-11 rounded hover:bg-n-slate-3 transition-colors"
              @click="resetFilters"
            >
              {{ $t('AUDIT_LOGS.FILTERS.RESET') }}
            </button>
            <button
              :disabled="uiFlags.exportingCsv"
              class="px-3 py-1.5 text-sm font-medium border border-n-slate-6 text-n-slate-11 rounded hover:bg-n-slate-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="exportCsv"
            >
              {{
                uiFlags.exportingCsv
                  ? $t('AUDIT_LOGS.FILTERS.EXPORTING')
                  : $t('AUDIT_LOGS.FILTERS.EXPORT_CSV')
              }}
            </button>
          </div>
        </div>

        <!-- Table -->
        <BaseTable :headers="tableHeaders" :items="records">
          <template #row="{ items }">
            <template v-for="auditLogItem in items" :key="auditLogItem.id">
              <BaseTableRow :item="auditLogItem">
                <template #default>
                  <!-- Actor -->
                  <BaseTableCell class="w-40">
                    <div class="flex flex-col">
                      <span class="text-body-main text-n-slate-12 font-medium">
                        {{ getActorName(auditLogItem) }}
                      </span>
                      <span
                        v-if="getActorEmail(auditLogItem)"
                        class="text-xs text-n-slate-10 truncate max-w-36"
                      >
                        {{ getActorEmail(auditLogItem) }}
                      </span>
                    </div>
                  </BaseTableCell>

                  <!-- Resource -->
                  <BaseTableCell class="w-44">
                    <div class="flex flex-col">
                      <span class="text-body-main text-n-slate-11">
                        {{ getResourceLabel(auditLogItem) }}
                      </span>
                      <span
                        v-if="getResourceName(auditLogItem)"
                        class="text-xs text-n-slate-10 truncate max-w-40"
                      >
                        {{ getResourceName(auditLogItem) }}
                      </span>
                    </div>
                  </BaseTableCell>

                  <!-- Activity -->
                  <BaseTableCell>
                    <div class="flex flex-col gap-1">
                      <span class="text-body-main text-n-slate-12">
                        {{ generateLogText(auditLogItem) }}
                      </span>
                      <button
                        v-if="hasChanges(auditLogItem)"
                        class="text-xs text-n-brand hover:underline text-left w-fit"
                        @click="toggleExpand(auditLogItem.id)"
                      >
                        {{
                          expandedRows.has(auditLogItem.id)
                            ? $t('AUDIT_LOGS.CHANGES.HIDE')
                            : $t('AUDIT_LOGS.CHANGES.SHOW')
                        }}
                      </button>
                    </div>
                  </BaseTableCell>

                  <!-- Time -->
                  <BaseTableCell class="w-36">
                    <span
                      class="text-body-main text-n-slate-11 whitespace-nowrap"
                    >
                      {{
                        messageTimestamp(
                          auditLogItem.created_at,
                          'MMM dd, yyyy hh:mm a'
                        )
                      }}
                    </span>
                  </BaseTableCell>

                  <!-- IP -->
                  <BaseTableCell class="w-36">
                    <span class="text-body-main text-n-slate-11">
                      {{ auditLogItem.remote_address }}
                    </span>
                  </BaseTableCell>
                </template>
              </BaseTableRow>

              <!-- Expanded changes row -->
              <tr
                v-if="expandedRows.has(auditLogItem.id)"
                :key="`${auditLogItem.id}-changes`"
              >
                <td colspan="5" class="py-3 px-4 bg-n-slate-2">
                  <div class="text-xs font-medium text-n-slate-11 mb-2">
                    {{ $t('AUDIT_LOGS.CHANGES.TITLE') }}
                  </div>
                  <table
                    class="w-full text-xs border-collapse border border-n-slate-5 rounded"
                  >
                    <thead>
                      <tr class="bg-n-slate-3">
                        <th
                          class="text-left px-3 py-1.5 text-n-slate-11 font-medium border-b border-n-slate-5 w-40"
                        >
                          {{ $t('AUDIT_LOGS.CHANGES.FIELD') }}
                        </th>
                        <th
                          class="text-left px-3 py-1.5 text-n-slate-11 font-medium border-b border-n-slate-5"
                        >
                          {{ $t('AUDIT_LOGS.CHANGES.FROM') }}
                        </th>
                        <th
                          class="text-left px-3 py-1.5 text-n-slate-11 font-medium border-b border-n-slate-5"
                        >
                          {{ $t('AUDIT_LOGS.CHANGES.TO') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="change in getChangePairs(auditLogItem)"
                        :key="change.key"
                        class="border-b border-n-slate-5 last:border-0"
                      >
                        <td class="px-3 py-1.5 text-n-slate-11 font-mono">
                          {{ change.key }}
                        </td>
                        <td class="px-3 py-1.5 text-n-ruby-10">
                          {{ formatChangeValue(change.before) }}
                        </td>
                        <td class="px-3 py-1.5 text-n-teal-10">
                          {{ formatChangeValue(change.after) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </template>
        </BaseTable>

        <PaginationFooter
          :current-page="Number(meta.currentPage)"
          :total-items="meta.totalEntries"
          :items-per-page="meta.perPage"
          class="!px-0"
          @update:current-page="onPageChange"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
